import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Container, Card, CardBody, Label, Input, FormFeedback, Form } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

import Breadcrumb from 'components/App/Breadcrumb';
import Meta from 'components/App/Meta';
import AppButton from 'components/App/Button';
import AppLoading from 'components/App/Loading';
import OtpInput from 'components/App/OtpInput';

import { checkHadError, validationErrorHandler } from 'utils';
import { passwordValidation as pwdValidation } from 'utils/validate';
import styles from './ChangePassword.module.scss';

const ChangePassword = (props) => {
  const {
    // me,
    profilePasswordError,
    profileLoadingPassword,
    actionUpdateMyPassword,
    actionUpdateMyPasswordFailed,
  } = props;

  const lastProcessedErrorRef = useRef(null);
  const history = useHistory();
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState('');
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowNewPassword, setIsShowNewPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);

  const onResendOtp = useCallback(() => {
    toast.success('Mã OTP đã được gửi lại thành công!');
    setOtp('');
  }, []);

  const onChangeOtpInput = useCallback((value) => {
    setOtp(value);
  }, []);

  const onSubmitOtp = useCallback(() => {
    if (otp.length < 6) {
      toast.error('Vui lòng nhập đầy đủ mã OTP gồm 6 chữ số');
      return;
    }
    setStep(3);
  }, [otp]);

  const onToggleShowPassword = useCallback(() => {
    setIsShowPassword((prev) => !prev);
  }, []);

  const onToggleShowNewPassword = useCallback(() => {
    setIsShowNewPassword((prev) => !prev);
  }, []);

  const onToggleShowConfirmPassword = useCallback(() => {
    setIsShowConfirmPassword((prev) => !prev);
  }, []);

  const validationStep1 = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Email không đúng định dạng')
        .required('Email không được để trống'),
      password: Yup.string().required('Mật khẩu không được để trống'),
    }),
    onSubmit: () => {
      setStep(2);
    },
  });

  const validationStep2 = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      newPassword: pwdValidation.newPassword,
      confirmPassword: pwdValidation.confirmPassword,
    }),
    onSubmit: (values) => {
      const callback = () => {
        validationStep1.resetForm();
        validationStep2.resetForm();
        setOtp('');
        setStep(1);
        history.push('/profile');
      };

      actionUpdateMyPassword({
        userPwd: {
          password: validationStep1.values.password,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        },
        callback,
      });
    },
  });

  // Clear any existing password errors in Redux store when mounting or unmounting
  useEffect(() => {
    actionUpdateMyPasswordFailed();
    return () => {
      actionUpdateMyPasswordFailed();
    };
  }, [actionUpdateMyPasswordFailed]);

  // Handle server-side errors mapping back to correct step/field
  useEffect(() => {
    if (
      profilePasswordError &&
      profilePasswordError !== lastProcessedErrorRef.current
    ) {
      lastProcessedErrorRef.current = profilePasswordError;

      if (profilePasswordError.password) {
        validationStep1.setErrors({
          password:
            validationErrorHandler(profilePasswordError.password) ||
            'Mật khẩu hiện tại không chính xác',
        });
        setStep(1);
      }
      if (profilePasswordError.newPassword) {
        validationStep2.setErrors({
          newPassword: validationErrorHandler(
            profilePasswordError.newPassword,
          ),
        });
        setStep(3);
      }
      if (profilePasswordError.confirmPassword) {
        validationStep2.setErrors({
          confirmPassword: validationErrorHandler(
            profilePasswordError.confirmPassword,
          ),
        });
        setStep(3);
      }
    }
  }, [
    profilePasswordError,
    validationStep1,
    validationStep2,
  ]);

  const breadcrumbList = useMemo(() => [
    {
      name: 'Đổi mật khẩu',
      isActive: true,
      link: '',
    },
  ], []);

  const onSubmitStep1 = useCallback((e) => {
    e.preventDefault();
    validationStep1.handleSubmit();
  }, [validationStep1]);

  const onSubmitStep2 = useCallback((e) => {
    e.preventDefault();
    validationStep2.handleSubmit();
  }, [validationStep2]);

  return (
    <div className="page-content">
      <Meta name="Đổi mật khẩu" />

      <Container fluid>
        <Breadcrumb
          pageTitle="ĐỐI MẬT KHẨU"
          breadcrumbList={breadcrumbList}
        />

        <div className={styles.Page}>
          <Card className={styles.CardWrapper}>
            <CardBody className="position-relative">
              <AppLoading loading={profileLoadingPassword} loaderOnPage />

              {step === 1 && (
                <Form onSubmit={onSubmitStep1}>
                  <h4 className={styles.Title}>Vui lòng đăng nhập để xác minh</h4>

                  <div className={styles.Field}>
                    <Label className={styles.Label}>
                      Email đăng nhập
                      <code>*</code>
                    </Label>
                    <Input
                      name="email"
                      type="text"
                      placeholder="Nhập tên đăng nhập"
                      onChange={validationStep1.handleChange}
                      onBlur={validationStep1.handleBlur}
                      value={validationStep1.values.email}
                      invalid={checkHadError(validationStep1, 'email')}
                      className={styles.Input}
                    />
                    {checkHadError(validationStep1, 'email') && (
                      <FormFeedback type="invalid" className="d-block mt-1">
                        {validationStep1.errors.email}
                      </FormFeedback>
                    )}
                  </div>

                  <div className={styles.Field}>
                    <Label className={styles.Label}>
                      Mật khẩu
                      <code>*</code>
                    </Label>
                    <Input
                      name="password"
                      type={isShowPassword ? 'text' : 'password'}
                      placeholder="Nhập mật khẩu"
                      onChange={validationStep1.handleChange}
                      onBlur={validationStep1.handleBlur}
                      value={validationStep1.values.password}
                      invalid={checkHadError(validationStep1, 'password')}
                      className={styles.Input}
                    />
                    <i
                      className={`fas ${isShowPassword ? 'fa-eye-slash' : 'fa-eye'} ${styles.ShowPassIcon}`}
                      onClick={onToggleShowPassword}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onToggleShowPassword();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Hiển thị mật khẩu"
                    />
                    {checkHadError(validationStep1, 'password') && (
                      <FormFeedback type="invalid" className="d-block mt-1">
                        {validationStep1.errors.password}
                      </FormFeedback>
                    )}
                  </div>

                  <AppButton
                    type="submit"
                    classes={styles.BtnSubmit}
                    loading={profileLoadingPassword}
                  >
                    KẾ TIẾP
                  </AppButton>
                </Form>
              )}

              {step === 2 && (
                <div>
                  <h4 className={styles.Title}>Xác minh tài khoản</h4>

                  <div className={styles.OtpField}>
                    <div className={styles.OtpLabel}>Nhập mã OTP</div>
                    <div className={styles.OtpWrapper}>
                      <OtpInput
                        value={otp}
                        valueLength={6}
                        onChange={onChangeOtpInput}
                      />
                    </div>
                    <div className={styles.ResendText}>
                      Chưa nhận được mã OTP?
                      <span
                        onClick={onResendOtp}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onResendOtp();
                          }
                        }}
                      >
                        GỬI LẠI
                      </span>
                    </div>
                  </div>

                  <AppButton
                    type="button"
                    classes={styles.BtnSubmit}
                    onClick={onSubmitOtp}
                  >
                    LƯU
                  </AppButton>
                </div>
              )}

              {step === 3 && (
                <Form onSubmit={onSubmitStep2}>
                  <h4 className={styles.Title}>Đổi mật khẩu mới</h4>

                  <div className={styles.Field}>
                    <Label className={styles.Label}>
                      Mật khẩu mới
                      <code>*</code>
                    </Label>
                    <Input
                      name="newPassword"
                      type={isShowNewPassword ? 'text' : 'password'}
                      placeholder="Nhập mật khẩu mới"
                      onChange={validationStep2.handleChange}
                      onBlur={validationStep2.handleBlur}
                      value={validationStep2.values.newPassword}
                      invalid={checkHadError(validationStep2, 'newPassword')}
                      className={styles.Input}
                    />
                    <i
                      className={`fas ${isShowNewPassword ? 'fa-eye-slash' : 'fa-eye'} ${styles.ShowPassIcon}`}
                      onClick={onToggleShowNewPassword}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onToggleShowNewPassword();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Hiển thị mật khẩu mới"
                    />
                    {checkHadError(validationStep2, 'newPassword') && (
                      <FormFeedback type="invalid" className="d-block mt-1">
                        {validationErrorHandler(validationStep2.errors.newPassword)}
                      </FormFeedback>
                    )}
                  </div>

                  <div className={styles.Field}>
                    <Label className={styles.Label}>
                      Xác nhận mật khẩu mới
                      <code>*</code>
                    </Label>
                    <Input
                      name="confirmPassword"
                      type={isShowConfirmPassword ? 'text' : 'password'}
                      placeholder="Nhập lại mật khẩu mới"
                      onChange={validationStep2.handleChange}
                      onBlur={validationStep2.handleBlur}
                      value={validationStep2.values.confirmPassword}
                      invalid={checkHadError(validationStep2, 'confirmPassword')}
                      className={styles.Input}
                    />
                    <i
                      className={`fas ${isShowConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} ${styles.ShowPassIcon}`}
                      onClick={onToggleShowConfirmPassword}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onToggleShowConfirmPassword();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Xác nhận mật khẩu mới"
                    />
                    {checkHadError(validationStep2, 'confirmPassword') && (
                      <FormFeedback type="invalid" className="d-block mt-1">
                        {validationErrorHandler(validationStep2.errors.confirmPassword)}
                      </FormFeedback>
                    )}
                  </div>

                  <div className={`${styles.ActionWrapper} d-flex justify-content-between gap-3 mt-4`}>
                    <AppButton
                      type="submit"
                      classes={`${styles.BtnSubmit} mt-0`}
                      loading={profileLoadingPassword}
                    >
                      LƯU
                    </AppButton>
                  </div>
                </Form>
              )}
            </CardBody>
          </Card>
        </div>
      </Container>
    </div>
  );
};

ChangePassword.propTypes = {
  // me: PropTypes.instanceOf(Object).isRequired,
  profilePasswordError: PropTypes.instanceOf(Object).isRequired,
  profileLoadingPassword: PropTypes.bool.isRequired,
  actionUpdateMyPassword: PropTypes.func.isRequired,
  actionUpdateMyPasswordFailed: PropTypes.func.isRequired,
};

export default ChangePassword;
